import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  createContext,
  useContext,
  useState,
} from "react";
import {
  ButtonProps,
  DateInputProps,
  DropdownProps,
  MultiselectProps,
  OrFormProps,
  OrFormRef,
  SwitchProps,
  TextAreaProps,
  TextFieldProps,
} from "./or-form.types";
import {
  AtButton,
  AtButtonSize,
  AtButtonType,
  AtDateInput,
  AtIcon,
  AtTextField,
  AtDropdown,
  AtSwitch,
  AtMultiselect,
} from "../../atoms";
import { ReactNode, useCallback } from "react";
import {
  useForm,
  FormProvider,
  useFormContext,
  UseFormReturn,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { buildValidationSchema } from "./build-validation";
import { CLEAN_FIELDS_BUTTON_TEXT, SPAN_CLASSES } from "./or-form.const";
import { AtTextarea } from "../../atoms/at-textarea/at-textarea";

// Context for form loading state and external errors
const FormLoadingContext = createContext<{
  isLoading: boolean;
  isDisabledByExternalError: boolean;
}>({
  isLoading: false,
  isDisabledByExternalError: false,
});

// Shared validation logic
const getFormValidationState = (
  formContext: UseFormReturn<Record<string, unknown>>,
) => {
  const {
    formState: { isValid, isDirty, isSubmitted },
    watch,
  } = formContext;
  const formValues = watch();
  const hasData = Object.values(formValues || {}).some(
    (value) => value !== null && value !== undefined && value !== "",
  );

  return {
    isDisabled: !isValid || !isDirty || !hasData,
    hasData,
    isSubmitted,
  };
};

const getGridClasses = (totalColumns: number) => {
  const gridClasses = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
    7: "grid-cols-7",
    8: "grid-cols-8",
    9: "grid-cols-9",
    10: "grid-cols-10",
  };
  const cols = Math.min(Math.max(totalColumns, 1), 10);
  return gridClasses[cols as keyof typeof gridClasses];
};

type BuildEmptyValuesObject =
  | Record<string, unknown>
  | string
  | boolean
  | null
  | undefined
  | unknown[];

const buildEmptyValues = (
  obj: BuildEmptyValuesObject,
): Record<string, unknown> | null | undefined => {
  if (obj === null || obj === undefined) return obj;

  // Arrays at top level shouldn't happen in form values, but handle it safely
  if (Array.isArray(obj)) {
    return undefined;
  }

  if (typeof obj === "object") {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const value = obj[key];
        // Handle nested arrays (like multiselect values)
        if (Array.isArray(value)) {
          acc[key] = [];
        } else {
          acc[key] = buildEmptyValues(value as BuildEmptyValuesObject);
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }

  return undefined;
};

const OrFormBase = React.forwardRef<OrFormRef, OrFormProps>(
  (
    {
      title,
      description,
      children,
      onSubmit,
      onClear,
      initialValues = {},
      "data-testid": testId,
      onStateChange,
      isLoading = false,
      hasExternalError = false,
      validateOnFirstLoad = false,
    },
    ref,
  ) => {
    // Track if submit button should be disabled due to external error
    const [isDisabledByExternalError, setIsDisabledByExternalError] =
      useState(false);

    // Build the validation schema based on the children components
    const autoSchema = useMemo(() => {
      return buildValidationSchema(children);
    }, [children]);

    const methods = useForm({
      defaultValues: initialValues,
      mode: "onChange",
      resolver: autoSchema ? yupResolver(autoSchema) : undefined,
    });

    const onSubmitForm = (data: Record<string, unknown>) => {
      if (onSubmit) {
        onSubmit(data);
      }
    };

    const handleClear = useCallback(() => {
      const emptyValues = buildEmptyValues(methods.getValues());
      methods.reset(emptyValues ? emptyValues : undefined);
      onClear?.();
    }, [methods, onClear]);

    useImperativeHandle(ref, () => ({
      submit: methods.handleSubmit(onSubmitForm),
    }));

    // Get validation state using shared function
    const { isDisabled, hasData, isSubmitted } =
      getFormValidationState(methods);

    useEffect(() => {
      onStateChange?.({
        disabled: isDisabled || isDisabledByExternalError,
        hasData,
        isSubmitted,
      });
      // Validate function dependencies generate loops
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDisabled, isDisabledByExternalError]);

    // Handle external error: disable button when external error occurs
    useEffect(() => {
      if (hasExternalError) {
        setIsDisabledByExternalError(true);
      }
    }, [hasExternalError]);

    // Re-enable button after first form change following an external error
    useEffect(() => {
      if (isDisabledByExternalError) {
        const subscription = methods.watch(() => {
          setIsDisabledByExternalError(false);
        });
        return () => subscription.unsubscribe();
      }
    }, [isDisabledByExternalError, methods]);

    useEffect(() => {
      if (validateOnFirstLoad && hasData && initialValues) {
        methods.trigger(); // force validation for initial values
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <FormProvider {...methods}>
        <FormLoadingContext.Provider
          value={{ isLoading, isDisabledByExternalError }}
        >
          <div data-testid={testId} className="w-full flex flex-col gap-4">
            {title && <Title title={title} />}
            {description && (
              <Description
                description={description}
                onClear={handleClear}
                hideOnClear={!hasData}
              />
            )}
            <form
              id={testId}
              noValidate
              onSubmit={methods.handleSubmit(onSubmitForm)}
              className="flex flex-col gap-4"
            >
              {children}
            </form>
          </div>
        </FormLoadingContext.Provider>
      </FormProvider>
    );
  },
);

OrFormBase.displayName = "OrForm";

const Title = ({ title }: { title?: OrFormProps["title"] }) => {
  if (!title) return null;
  const { icon, text } = title;

  return (
    <div className="flex items-center gap-2">
      <div className="w-10 h-10 flex items-center justify-center bg-primary-75 rounded-full">
        <AtIcon name={icon} className="text-primary-500" />
      </div>
      <p className="text-xl font-semibold text-primary-400">{text}</p>
    </div>
  );
};

export const FallbackForm = ({
  cols: totalColumns,
  length,
}: {
  cols: number;
  length: number;
}) => {
  const gridClasses = useMemo(() => {
    return getGridClasses(totalColumns);
  }, [totalColumns]);

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-surface-75 h-5 w-1/2 animate-pulse"></div>
      <div className={`grid ${gridClasses} gap-4`}>
        {Array.from({ length }).map((_, index) => (
          <div
            key={index}
            className="bg-surface-75 h-10 rounded animate-pulse"
          ></div>
        ))}
      </div>
    </div>
  );
};

const Description = ({
  description,
  onClear,
  hideOnClear = false,
}: {
  description: ReactNode;
  onClear: () => void;
  hideOnClear: boolean;
}) => {
  return (
    <div
      className={`flex items-center justify-between ${hideOnClear ? "!py-2" : ""}`}
    >
      {description && <span className="">{description}</span>}

      <AtButton
        className={`${hideOnClear ? "!hidden" : ""}`}
        buttonType={AtButtonType.TERTIARY}
        size={AtButtonSize.MEDIUM}
        data-testid="clear-fields-button"
        text={CLEAN_FIELDS_BUTTON_TEXT}
        onClick={onClear}
      />
    </div>
  );
};

const Row = ({
  children,
  className = "",
}: {
  children: ReactNode;
  spacing?: number;
  className?: string;
}) => {
  // Memoize the total columns calculation
  const totalColumns = useMemo(() => {
    let total = 0;
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const span = (child.props as { span?: number }).span || 1;
        total += span;
      }
    });
    return Math.max(total, 1);
  }, [children]);

  const gridClasses = useMemo(() => {
    return getGridClasses(totalColumns);
  }, [totalColumns]);

  return (
    <div className={`grid ${gridClasses} gap-4 ${className}`}>{children}</div>
  );
};
const TextField = ({
  className = "",
  name,
  span = 1,
  ...textFieldProps
}: TextFieldProps) => {
  const {
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const error = errors[name];
  const value = watch(name) || "";

  const handleChange = (newValue: string) => {
    setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <div className={`${SPAN_CLASSES[span]} ${className}`}>
      <AtTextField
        {...textFieldProps}
        required={!!textFieldProps.validation?.required}
        value={value}
        onChange={handleChange}
        error={!!error}
        helpText={error ? (error?.message as string) : ""}
      />
    </div>
  );
};

const TextArea = ({
  className = "",
  name,
  span = 1,
  helpText,
  ...textFieldProps
}: TextAreaProps) => {
  const {
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const error = errors[name];
  const value = watch(name) || "";

  const handleChange = (newValue: string) => {
    setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <div className={`${SPAN_CLASSES[span]} ${className}`}>
      <AtTextarea
        {...textFieldProps}
        required={!!textFieldProps.validation?.required}
        value={value}
        onChange={handleChange}
        error={!!error}
        helpText={error ? (error?.message as string) : helpText}
        maxChars={textFieldProps.validation?.max?.value}
      />
    </div>
  );
};
const Dropdown = ({
  className = "",
  name,
  span = 1,
  ...dropdownProps
}: DropdownProps) => {
  const {
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const error = errors[name];
  const value = watch(name) || "";

  const handleChange = (newValue: string) => {
    setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <div className={`${SPAN_CLASSES[span]} ${className}`}>
      <AtDropdown
        {...dropdownProps}
        required={!!dropdownProps.validation?.required}
        value={value}
        onChange={handleChange}
        error={!!error}
        helpText={error ? (error?.message as string) : ""}
      />
    </div>
  );
};
const Multiselect = ({
  className = "",
  name,
  span = 1,
  ...multiselectProps
}: MultiselectProps) => {
  const {
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const error = errors[name];
  const value = watch(name) || [];

  const handleChange = (newValue: string[]) => {
    setValue(name, newValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <div className={`${SPAN_CLASSES[span]} ${className}`}>
      <AtMultiselect
        {...multiselectProps}
        required={!!multiselectProps.validation?.required}
        value={value}
        onChange={handleChange}
        error={!!error}
        helpText={error ? (error?.message as string) : ""}
      />
    </div>
  );
};
const Switch = ({
  className = "",
  name,
  span = 1,
  onChange,
  ...switchProps
}: SwitchProps) => {
  const { watch, setValue } = useFormContext();
  const value = watch(name) || "";

  const handleChange = (newValue: React.ChangeEvent<HTMLInputElement>) => {
    setValue(name, newValue.target.checked, {
      shouldValidate: true,
      shouldDirty: true,
    });
    onChange?.(newValue);
  };

  return (
    <div className={`${SPAN_CLASSES[span]} ${className}`}>
      <AtSwitch
        {...switchProps}
        required={!!switchProps.validation?.required}
        checked={value}
        onChange={(value) => handleChange(value)}
      />
    </div>
  );
};

const DateInput = ({
  className = "",
  name,
  span = 1,
  ...dateInputProps
}: DateInputProps) => {
  const {
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const error = errors[name];
  const rawValue = watch(name);

  // Normalize the value: ensure it's either a valid Date object or null
  const value = (() => {
    if (!rawValue) return null;
    if (rawValue instanceof Date) {
      // Check if it's a valid Date object
      return !isNaN(rawValue.getTime()) ? rawValue : null;
    }
    // If it's not a Date object, it's invalid - clean it up
    if (rawValue !== null && rawValue !== undefined) {
      setValue(name, null, { shouldValidate: false, shouldDirty: false });
    }
    return null;
  })();

  const handleChange = (newValue: Date | null) => {
    setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <div className={`${SPAN_CLASSES[span]} ${className}`}>
      <AtDateInput
        {...dateInputProps}
        required={!!dateInputProps.validation?.required}
        value={value}
        onChange={handleChange}
        error={!!error}
        helpText={error ? (error?.message as string) : ""}
      />
    </div>
  );
};
const Button = ({
  className = "",
  type = "submit",
  span = 1,
  loading = false,
  ...buttonProps
}: ButtonProps) => {
  const formContext = useFormContext();
  const { isLoading: formLoading, isDisabledByExternalError } =
    useContext(FormLoadingContext);
  const { isDisabled } = getFormValidationState(formContext);
  const isButtonDisabled =
    type === "submit" && (isDisabled || isDisabledByExternalError);

  // Use form loading state for submit buttons, or explicit loading prop for other buttons
  const buttonLoading = type === "submit" ? formLoading : loading;

  return (
    <div className={`${SPAN_CLASSES[span]} ${className}`}>
      <AtButton
        {...buttonProps}
        loading={buttonLoading}
        disabled={isButtonDisabled}
        className="w-full"
        size={AtButtonSize.MEDIUM}
        type={type}
      />
    </div>
  );
};

type OrFormComponent = React.ForwardRefExoticComponent<
  OrFormProps & React.RefAttributes<OrFormRef>
> & {
  TextField: typeof TextField;
  TextArea: typeof TextArea;
  DateInput: typeof DateInput;
  Dropdown: typeof Dropdown;
  Multiselect: typeof Multiselect;
  Switch: typeof Switch;
  Button: typeof Button;
  Row: typeof Row;
};

export const OrForm = OrFormBase as OrFormComponent;

OrForm.TextField = TextField;
OrForm.TextArea = TextArea;
OrForm.DateInput = DateInput;
OrForm.Dropdown = Dropdown;
OrForm.Multiselect = Multiselect;
OrForm.Switch = Switch;
OrForm.Button = Button;
OrForm.Row = Row;
